import { useState, useEffect, Fragment } from "react";
import { collection, doc, setDoc, deleteDoc, serverTimestamp, query, getDocs } from "firebase/firestore";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES } from "../../config/constants";
import { db } from "../../firebase";
import useMobile from "../../hooks/useMobile";
import { msCall } from "../../utils/api";
import { btnP, btnS } from "../../utils/styles";

export default function OneDrivePage({ clients, theme, mobile }) {
  const t = THEMES[theme];
  const _isMobile = useMobile();
  const isMobile = mobile || _isMobile;
  const [search, setSearch] = useState("");
  const [filterTab, setFilterTab] = useState("all");
  const [openFolder, setOpenFolder] = useState(null);
  const [folderFiles, setFolderFiles] = useState({});
  const [filesLoading, setFilesLoading] = useState({});
  const [linkedMap, setLinkedMap] = useState({});
  const [manualLinkFolder, setManualLinkFolder] = useState(null);
  const [manualLinkSearch, setManualLinkSearch] = useState("");
  const [msStatus, setMsStatus] = useState(null);
  const [odFolders, setOdFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [driveId, setDriveId] = useState(null);

  useEffect(() => {
    checkMsStatus();
    loadLinkedMap();
  }, []);

  // Load persisted link map from Firestore
  async function loadLinkedMap() {
    try {
      const snap = await getDocs(query(collection(db, "onedrive_links")));
      const map = {};
      snap.forEach(d => { map[d.id] = d.data().clientId; });
      setLinkedMap(map);
    } catch (e) { console.error("loadLinkedMap:", e.message); }
  }

  // Persist a link to Firestore
  const SP_BASE = "https://netsavetaxaccounting559.sharepoint.com/sites/team/Shared%20Documents/Forms/AllItems.aspx";

  async function persistLink(folderId, clientId, folderName, webUrl) {
    try {
      await setDoc(doc(db, "onedrive_links", folderId), { clientId, folderName: folderName || "", webUrl: webUrl || "", linkedAt: serverTimestamp() });
    } catch (e) { console.error("persistLink:", e.message); }
  }

  // Remove a link from Firestore
  async function removeLink(folderId) {
    try {
      await deleteDoc(doc(db, "onedrive_links", folderId));
    } catch (e) { console.error("removeLink:", e.message); }
  }

  // Wrapper: update state + persist
  function linkFolder(folderId, clientId, folderName, webUrl) {
    setLinkedMap(m => ({ ...m, [folderId]: clientId }));
    persistLink(folderId, clientId, folderName, webUrl);
  }

  function unlinkFolder(folderId) {
    setLinkedMap(m => { const n = { ...m }; delete n[folderId]; return n; });
    removeLink(folderId);
  }

  async function checkMsStatus() {
    try {
      const data = await msCall("msGetStatus", {}, "GET");
      setMsStatus(data.connected ? data : false);
      if (data.connected) fetchFolders();
    } catch { setMsStatus(false); }
  }

  async function fetchFolders() {
    setFoldersLoading(true);
    try {
      const data = await msCall("msFetchOneDrive", {}, "GET");
      const folders = (data.items || []).filter(i => i.folder);
      setOdFolders(folders);
      if (data.driveId) setDriveId(data.driveId);
      // Backfill webUrl on existing linked folders that don't have it
      try {
        const snap = await getDocs(query(collection(db, "onedrive_links")));
        snap.forEach(d => {
          if (!d.data().webUrl) {
            const folder = folders.find(f => f.id === d.id);
            if (folder && folder.webUrl) {
              setDoc(doc(db, "onedrive_links", d.id), { webUrl: folder.webUrl, folderName: folder.name || d.data().folderName || "" }, { merge: true });
            }
          }
        });
      } catch (e) { console.warn("webUrl backfill:", e.message); }
    } catch (e) { console.error("fetchFolders:", e.message); }
    finally { setFoldersLoading(false); }
  }

  async function fetchFolderFiles(folderId) {
    if (folderFiles[folderId]) return;
    setFilesLoading(fl => ({ ...fl, [folderId]: true }));
    try {
      const params = { folderId };
      if (driveId) params.driveId = driveId;
      const data = await msCall("msFetchOneDriveFolder", params, "GET");
      setFolderFiles(ff => ({ ...ff, [folderId]: data.items || [] }));
    } catch (e) { console.error("fetchFolderFiles:", e.message); }
    finally { setFilesLoading(fl => ({ ...fl, [folderId]: false })); }
  }

  async function handleConnect() {
    setConnectLoading(true);
    try {
      const data = await msCall("msGetAuthUrl", {}, "GET");
      window.location.href = data.url;
    } catch (e) { alert("Error: " + e.message); setConnectLoading(false); }
  }

  async function handleDisconnect() {
    if (!window.confirm("Disconnect Microsoft account?")) return;
    await msCall("msDisconnect", {});
    setMsStatus(false);
    setOdFolders([]);
  }

  function toggleFolder(folderId) {
    const isOpen = openFolder === folderId;
    setOpenFolder(isOpen ? null : folderId);
    if (!isOpen && msStatus) fetchFolderFiles(folderId);
  }

  // Fuzzy match folders to CRM clients
  const matchScore = (folderName, client) => {
    const fn = folderName.toLowerCase();
    const cn = (client.name || "").toLowerCase();
    const sn = (client.spouseName || "").toLowerCase();
    const biz = (client.businesses || []).map(b => (b.legalName || "").toLowerCase());
    const sim = (a, b) => {
      if (!a || !b) return 0;
      const longer = a.length > b.length ? a : b;
      const shorter = a.length > b.length ? b : a;
      if (longer.length === 0) return 1;
      let matches = 0;
      for (let i = 0; i < shorter.length; i++) if (longer.includes(shorter[i])) matches++;
      return matches / longer.length;
    };
    let score = sim(fn, cn) * 100;
    if (sn) score = Math.max(score, sim(fn, sn) * 80);
    biz.forEach(b => { score = Math.max(score, sim(fn, b) * 90); });
    return Math.round(score);
  };

  const displayFolders = msStatus ? odFolders : [];
  const linkedCount = Object.keys(linkedMap).length;
  const filteredFolders = displayFolders.filter(f => {
    const ms = !search || f.name.toLowerCase().includes(search.toLowerCase());
    const linked = !!linkedMap[f.id];
    const tab = filterTab === "all" || (filterTab === "linked" && linked) || (filterTab === "unlinked" && !linked);
    return ms && tab;
  });

  function fileIcon(item) {
    if (item.folder) return "📁";
    const name = (item.name || "").toLowerCase();
    if (name.endsWith(".pdf")) return "📄";
    if (name.endsWith(".xlsx") || name.endsWith(".xls")) return "📊";
    if (name.endsWith(".docx") || name.endsWith(".doc")) return "📝";
    if (name.endsWith(".jpg") || name.endsWith(".png")) return "🖼️";
    return "📎";
  }

  function fmtSize(bytes) {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
    return `${(bytes/1024/1024).toFixed(1)} MB`;
  }

  return (
    <div>
      {/* Connection Card */}
      <div style={{ background: t.surface, border: `1px solid ${BRAND.blue}30`, borderRadius: 16, padding: 20, marginBottom: 20, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: "#0078d415", border: "2px solid #0078d440", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path d="M6.5 20C4.0 20 2 18.0 2 15.5C2 13.3 3.6 11.5 5.7 11.1C5.3 10.4 5 9.6 5 8.8C5 6.1 7.1 4 9.8 4C11.4 4 12.8 4.8 13.7 6C14.2 5.7 14.8 5.5 15.5 5.5C17.4 5.5 19 7.1 19 9C19 9.2 19 9.4 18.9 9.6C20.7 10.2 22 11.9 22 13.8C22 16.1 20.1 18 17.8 18L6.5 20Z" fill="#0078d4"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: t.text, marginBottom: 3 }}>Microsoft OneDrive</div>
          {msStatus
            ? <div style={{ fontSize: 12, color: t.textMuted }}>Connected as <strong>{msStatus.email}</strong> — {odFolders.length} folders loaded</div>
            : <div style={{ fontSize: 12, color: t.textMuted }}>Connect your Microsoft account to browse and manage client folders</div>
          }
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {msStatus ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, background: "#8DC63F18", border: "1px solid #8DC63F40", fontSize: 12, color: BRAND.green, fontWeight: 600 }}>✓ Connected</div>
              <button onClick={fetchFolders} style={{ ...btnS(t), fontSize: 12 }} disabled={foldersLoading}>{foldersLoading ? <><MiniSpinner size={12} color={t.text}/>Loading...</> : "↻ Refresh"}</button>
              <button onClick={handleDisconnect} style={{ padding: "7px 14px", borderRadius: 8, background: "transparent", border: "1px solid #ef444440", color: "#ef4444", fontSize: 12, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>Disconnect</button>
            </>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, background: "#f59e0b18", border: "1px solid #f59e0b40", fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>⚠ Not Connected</div>
              <button onClick={handleConnect} disabled={connectLoading} style={{ ...btnP, background: "linear-gradient(135deg,#0078d4,#106ebe)", display: "flex", alignItems: "center", gap: 6 }}>
                {connectLoading ? <><MiniSpinner size={13} color="#fff"/>Connecting...</> : <><span style={{fontSize:13}}>🔗</span> Connect Microsoft</>}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Preview banner */}
      {!msStatus && (
        <div style={{ background: theme === "dark" ? "#1a1200" : "#fffbeb", border: "1px solid #f59e0b40", borderRadius: 12, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#92400e" }}>
          <span style={{ fontSize: 16 }}>🚧</span>
          <span><strong>Preview UI</strong> — Connect Microsoft to load your real OneDrive folders. Sample data shown below.</span>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${isMobile?2:4},1fr)`, gap: isMobile?10:14, marginBottom: 20 }}>
        {[
          { l: "CRM Clients",    v: clients.length,                        c: BRAND.blue },
          { l: "OD Folders",     v: displayFolders.length,                 c: "#0078d4" },
          { l: "Linked",         v: linkedCount,                           c: BRAND.green },
          { l: "Unlinked",       v: displayFolders.length - linkedCount,   c: "#f59e0b" },
        ].map(s => (
          <div key={s.l} style={{ background: t.surface, border: `1px solid ${s.c}30`, borderRadius: 14, padding: isMobile?"12px 14px":"16px 20px", borderTop: `3px solid ${s.c}` }}>
            <div style={{ fontSize: isMobile?22:30, fontWeight: 800, color: s.c, letterSpacing: "-1px", marginBottom: 2 }}>{s.v}</div>
            <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Folder Table */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: isMobile?14:20 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: t.text }}>Client Folders → CRM Matching</div>
          <div style={{ display: "flex", alignItems: "center", background: t.inputBg, border: `1px solid ${t.border}`, borderRadius: 9, padding: "7px 12px", gap: 8, flex: 1, marginLeft: "auto" }}>
            <Icon name="search" size={14} color={t.textMuted} />
            <input style={{ background: "transparent", border: "none", outline: "none", color: t.text, fontSize: 13, width: "100%", fontFamily: "'Outfit',sans-serif" }} placeholder="Search folders..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, marginBottom: 14, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          {[["all","All"],["linked","Linked"],["unlinked","Unlinked"]].map(([val,label]) => (
            <button key={val} onClick={() => setFilterTab(val)} style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${filterTab===val?BRAND.blue+"50":t.border}`, background: filterTab===val?BRAND.blue+"15":t.bg, color: filterTab===val?BRAND.blue:t.textSub, fontFamily: "'Outfit',sans-serif", whiteSpace: "nowrap", flexShrink: 0 }}>{label}</button>
          ))}
        </div>

        {foldersLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: t.textMuted, fontSize: 13 }}>Loading OneDrive folders...</div>
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>{["OneDrive Folder","Last Modified","Items","Status","CRM Client","Actions"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: t.textMuted, fontWeight: 600, fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: `1px solid ${t.border}` }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {filteredFolders.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: "30px 0", textAlign: "center", color: t.textMuted, fontSize: 13 }}>No folders found</td></tr>
                )}
                {filteredFolders.map(folder => {
                  const isLinked = !!linkedMap[folder.id];
                  const linkedClient = isLinked ? clients.find(c => c.id === linkedMap[folder.id]) : null;
                  const suggestions = !isLinked ? clients.map(c => ({ ...c, score: matchScore(folder.name, c) })).filter(c => c.score >= 40).sort((a,b) => b.score - a.score) : [];
                  const top = suggestions[0];
                  const files = folderFiles[folder.id] || (msStatus ? null : []);
                  const isOpen = openFolder === folder.id;
                  const modified = folder.lastModifiedDateTime
                    ? new Date(folder.lastModifiedDateTime).toLocaleDateString()
                    : (folder.modified || "—");
                  const itemCount = folder.folder?.childCount ?? folder.size ?? "—";
                  return (
                    <Fragment key={folder.id}>
                      <tr style={{ background: isOpen ? (theme==="dark"?"#0e1a2e":"#f0f7ff") : "transparent" }}>
                        <td style={{ padding: "11px 12px", borderBottom: isOpen?"none":`1px solid ${t.borderLight}` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button onClick={() => toggleFolder(folder.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 16 }}>
                              {isOpen ? "📂" : "📁"}
                            </button>
                            <div style={{ fontWeight: 700, color: t.text, cursor: "pointer" }} onClick={() => toggleFolder(folder.id)}>{folder.name}</div>
                          </div>
                        </td>
                        <td style={{ padding: "11px 12px", borderBottom: isOpen?"none":`1px solid ${t.borderLight}`, fontSize: 12, color: t.textSub }}>{modified}</td>
                        <td style={{ padding: "11px 12px", borderBottom: isOpen?"none":`1px solid ${t.borderLight}` }}>
                          <span style={{ fontSize: 11.5, color: t.textMuted, background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "2px 8px" }}>{itemCount}</span>
                        </td>
                        <td style={{ padding: "11px 12px", borderBottom: isOpen?"none":`1px solid ${t.borderLight}` }}>
                          {isLinked
                            ? <span style={{ fontSize: 11, fontWeight: 700, color: BRAND.green, background: BRAND.green+"18", padding: "2px 9px", borderRadius: 10, border: `1px solid ${BRAND.green}30` }}>✓ Linked</span>
                            : <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", background: "#f59e0b18", padding: "2px 9px", borderRadius: 10, border: "1px solid #f59e0b30" }}>Not Linked</span>
                          }
                        </td>
                        <td style={{ padding: "11px 12px", borderBottom: isOpen?"none":`1px solid ${t.borderLight}` }}>
                          {isLinked ? (
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: t.text }}>{linkedClient?.name || "—"}</div>
                          ) : top ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <div style={{ fontSize: 11.5, color: t.textSub }}>{top.name}</div>
                              <span style={{ fontSize: 10, color: BRAND.blue, background: BRAND.blue+"18", padding: "1px 6px", borderRadius: 8, fontWeight: 700 }}>Suggested</span>
                            </div>
                          ) : <span style={{ fontSize: 11.5, color: t.textMuted }}>—</span>}
                        </td>
                        <td style={{ padding: "11px 12px", borderBottom: isOpen?"none":`1px solid ${t.borderLight}`, position: "relative" }}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {isLinked ? (
                              <button onClick={() => unlinkFolder(folder.id)} style={{ padding: "5px 10px", borderRadius: 7, background: "transparent", border: "1px solid #f8717140", cursor: "pointer", color: "#f87171", fontSize: 12, fontFamily: "'Outfit',sans-serif" }}>Unlink</button>
                            ) : (
                              <>
                                {top && <button onClick={() => linkFolder(folder.id, top.id, folder.name, folder.webUrl)} style={{ padding: "5px 10px", borderRadius: 7, background: BRAND.blue+"18", border: `1px solid ${BRAND.blue}40`, cursor: "pointer", color: BRAND.blue, fontSize: 12, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 4 }}><Icon name="check" size={12} color={BRAND.blue}/>Auto-Link</button>}
                                <button onClick={() => { setManualLinkFolder(folder.id); setManualLinkSearch(""); }} style={{ padding: "5px 10px", borderRadius: 7, background: "transparent", border: `1px solid ${t.border}`, cursor: "pointer", color: t.textSub, fontSize: 12, fontFamily: "'Outfit',sans-serif" }}>Manual</button>
                                {manualLinkFolder === folder.id && (
                                  <div style={{ position: "absolute", zIndex: 50, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 10, minWidth: 250, boxShadow: "0 8px 30px #0003", marginTop: 2 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 6 }}>Select CRM Client</div>
                                    <input type="text" value={manualLinkSearch} onChange={e=>setManualLinkSearch(e.target.value)} placeholder="Search client..." autoFocus style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid " + t.border, background: t.inputBg || t.bg, color: t.text, fontSize: 12, fontFamily: "'Outfit',sans-serif", outline: "none", marginBottom: 6, boxSizing: "border-box" }}/>
                                    <div style={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                                      {clients.filter(c => !manualLinkSearch || c.name?.toLowerCase().includes(manualLinkSearch.toLowerCase())).map(c => (
                                        <button key={c.id} onClick={() => { linkFolder(folder.id, c.id, folder.name, folder.webUrl); setManualLinkFolder(null); }}
                                          style={{ padding: "6px 10px", borderRadius: 7, background: "transparent", border: `1px solid ${t.border}`, cursor: "pointer", color: t.text, fontSize: 12, fontFamily: "'Outfit',sans-serif", textAlign: "left" }}>
                                          {c.name}
                                        </button>
                                      ))}
                                    </div>
                                    <button onClick={() => setManualLinkFolder(null)} style={{ marginTop: 8, width: "100%", padding: "5px", borderRadius: 7, background: "transparent", border: `1px solid ${t.border}`, cursor: "pointer", color: t.textMuted, fontSize: 11, fontFamily: "'Outfit',sans-serif" }}>Cancel</button>
                                  </div>
                                )}
                              </>
                            )}
                            <button onClick={() => toggleFolder(folder.id)} style={{ padding: "5px 10px", borderRadius: 7, background: "transparent", border: `1px solid ${t.border}`, cursor: "pointer", color: t.textSub, fontSize: 12, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                              <Icon name="eye" size={12} />{isOpen?"Hide":"Files"}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded file list */}
                      {isOpen && (
                        <tr>
                          <td colSpan={6} style={{ padding: "0 12px 14px 44px", borderBottom: `1px solid ${t.borderLight}`, background: theme==="dark"?"#0e1a2e":"#f0f7ff" }}>
                            {filesLoading[folder.id] ? (
                              <div style={{ fontSize: 12, color: t.textMuted, padding: "12px 0" }}>Loading files...</div>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                {(files || []).map((file, fi) => (
                                  <div key={fi} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", borderRadius: 8, background: t.surface, border: `1px solid ${t.border}` }}>
                                    <span style={{ fontSize: 16, flexShrink: 0 }}>{fileIcon(file)}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 12.5, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file.name}</div>
                                    </div>
                                    <div style={{ fontSize: 11, color: t.textMuted, whiteSpace: "nowrap" }}>{fmtSize(file.size)}</div>
                                    <div style={{ fontSize: 11, color: t.textMuted, whiteSpace: "nowrap" }}>{file.lastModifiedDateTime ? new Date(file.lastModifiedDateTime).toLocaleDateString() : file.modified || ""}</div>
                                    {file.webUrl ? (
                                      <a href={file.webUrl} target="_blank" rel="noopener noreferrer" style={{ padding: "3px 10px", borderRadius: 6, background: BRAND.blue+"15", border: `1px solid ${BRAND.blue}30`, color: BRAND.blue, fontSize: 11, fontWeight: 600, fontFamily: "'Outfit',sans-serif", whiteSpace: "nowrap", textDecoration: "none" }}>↗ Open</a>
                                    ) : (
                                      <button style={{ padding: "3px 10px", borderRadius: 6, background: BRAND.blue+"15", border: `1px solid ${BRAND.blue}30`, cursor: "not-allowed", color: BRAND.blue, fontSize: 11, fontWeight: 600, fontFamily: "'Outfit',sans-serif", opacity: 0.6, whiteSpace: "nowrap" }} disabled>↓ Download</button>
                                    )}
                                  </div>
                                ))}
                                {(!files || files.length === 0) && <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", padding: "8px 0" }}>No files in this folder.</div>}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
